/**
 * Cart Consolidation System - UNIVERSAL v5.0
 *
 * FIXES:
 * - v4.1: Manejo correcto del contexto de fetch
 * - v4.2: Recarga automática del cart drawer después de consolidación
 * - v4.3: Mejorado reload del drawer usando Shopify Sections API + logging detallado
 * - v4.4: Prevención de consolidación duplicada + corrección de errores 400 al eliminar items
 * - v4.5: Método simplificado y robusto de recarga del drawer con fetch directo del HTML
 * - v5.0: MAJOR - Verifica y actualiza variante automáticamente al cambiar cantidad (no solo duplicados)
 */

class CartConsolidation {
    constructor() {
      this.API_URL = 'https://melmx.lievant.com.mx/api/shopify/variant-cart';
      this.processing = false;
      this.consolidating = false; // Nueva bandera para prevenir consolidaciones duplicadas
      this.autoConsolidated = false;

      // CORRECCIÓN: Bind del contexto de fetch
      this.originalFetch = window.fetch.bind(window);

      this.init();
    }
  
    init() {
      console.log('🚀 Cart Consolidation System - UNIVERSAL v5.0 initializing...');
  
      // 1. Interceptar formularios
      document.addEventListener('submit', (e) => {
        const form = e.target;
        
        if (form.matches('form[action*="/cart/add"]')) {
          console.log('📝 Intercepted form submit');
          e.preventDefault();
          e.stopPropagation();
          this.handleFormSubmit(form);
        }
      }, true);
  
      // 2. Interceptar fetch
      this.interceptFetch();
  
      // 3. AUTO-CONSOLIDACIÓN en /cart
      if (window.location.pathname === '/cart') {
        console.log('📍 Detected cart page, checking for duplicates...');
        setTimeout(() => this.autoConsolidateCartPage(), 1000);
      }
  
      console.log('✅ Cart Consolidation System UNIVERSAL v5.0 initialized');
    }
  
    /**
     * Intercepta window.fetch - VERSIÓN CORREGIDA
     */
    interceptFetch() {
      const self = this;
      const originalFetch = this.originalFetch;
      
      window.fetch = function(url, options) {
        // Detectar si es un request a /cart/add.js
        const urlString = typeof url === 'string' ? url : url?.url || '';
        
        if (urlString.includes('/cart/add') || urlString === '/cart/add.js') {
          console.log('🎯 Intercepted fetch to:', urlString);
          
          // Obtener el body
          let body;
          try {
            if (options?.body) {
              body = typeof options.body === 'string' 
                ? JSON.parse(options.body) 
                : options.body;
            }
          } catch (e) {
            // Silent catch
          }
          
          if (body && body.id) {
            console.log('📦 Adding variant:', body.id, '× qty:', body.quantity || 1);
            
            // Dejar que se agregue normalmente primero
            // CORRECCIÓN: Usar call con contexto window
            return originalFetch.call(window, url, options).then(result => {
              if (result.ok) {
                console.log('✅ Added successfully, checking for consolidation...');
                
                setTimeout(() => {
                  self.checkAndConsolidate();
                }, 500);
              }
              
              return result;
            });
          }
        }
        
        // Para todos los demás requests
        // CORRECCIÓN: Usar call con contexto window
        return originalFetch.call(window, url, options);
      };
    }
  
    /**
     * Maneja submit de formulario
     */
    async handleFormSubmit(form) {
      if (this.processing) {
        console.log('⏳ Already processing...');
        return;
      }
  
      this.processing = true;
  
      try {
        const submitButton = form.querySelector('[type="submit"], [name="add"]');
        const originalText = submitButton?.textContent || submitButton?.value;
        
        if (submitButton) {
          submitButton.disabled = true;
          if (submitButton.textContent) {
            submitButton.textContent = 'Agregando...';
          } else {
            submitButton.value = 'Agregando...';
          }
        }
  
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity') || '1');
  
        console.log('📦 Adding to cart:', { variantId, quantity });
  
        // CORRECCIÓN: Ya no necesitamos this.originalFetch aquí porque
        // el fetch interceptado manejará esto
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity })
        });
  
        if (!addResponse.ok) {
          const errorData = await addResponse.json().catch(() => ({}));
          throw new Error(errorData.description || 'No se pudo agregar al carrito');
        }
  
        console.log('✅ Added successfully');
  
        // Revisar consolidación
        await this.checkAndConsolidate();
  
        this.showSuccess(submitButton, originalText);
  
      } catch (error) {
        console.error('❌ Error:', error);
        this.showError(form, error.message);
      } finally {
        this.processing = false;
      }
    }
  
    /**
     * Revisa y ajusta variantes según cantidad total (incluso sin duplicados)
     */
    async checkAndConsolidate() {
      // Prevenir ejecuciones duplicadas
      if (this.consolidating) {
        console.log('⏳ Consolidation already in progress, skipping...');
        return;
      }

      try {
        this.consolidating = true;

        const cart = await this.originalFetch('/cart.js').then(r => r.json());

        const byProduct = {};
        cart.items.forEach(item => {
          if (!byProduct[item.product_id]) {
            byProduct[item.product_id] = [];
          }
          byProduct[item.product_id].push(item);
        });

        // Revisar CADA producto, no solo los duplicados
        for (const [productId, items] of Object.entries(byProduct)) {
          const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

          console.log(`📦 Checking product ${productId}:`, {
            items: items.length,
            totalQuantity,
            variants: items.map(i => ({ variant_id: i.variant_id, variant_title: i.variant_title, qty: i.quantity }))
          });

          // Consultar cuál debería ser la variante correcta para esta cantidad
          const correctVariantId = await this.getCorrectVariant(productId, totalQuantity);

          if (!correctVariantId) {
            console.log('   ℹ️ No variant rule for this product');
            continue;
          }

          // Verificar si necesitamos cambiar la variante
          const needsConsolidation = items.length > 1; // Hay duplicados
          const currentVariantId = items[0].variant_id.toString();
          const needsVariantChange = currentVariantId !== correctVariantId && items.length === 1; // Solo 1 item pero variante incorrecta

          if (needsConsolidation) {
            console.log('🔄 Found duplicates, consolidating...');
            await this.consolidateProduct(productId, items);
            await this.updateCartUI();

            if (window.location.pathname === '/cart') {
              setTimeout(() => window.location.reload(), 500);
            }
            return;
          }

          if (needsVariantChange) {
            console.log(`🔄 Quantity changed, updating variant from ${currentVariantId} to ${correctVariantId}...`);
            await this.updateProductVariant(productId, items[0], correctVariantId, totalQuantity);
            await this.updateCartUI();

            if (window.location.pathname === '/cart') {
              setTimeout(() => window.location.reload(), 500);
            }
            return;
          }

          console.log('   ✅ Variant is correct for this quantity');
        }

        console.log('✅ All products have correct variants');
        await this.updateCartUI();

      } catch (error) {
        console.error('Error checking cart:', error);
      } finally {
        this.consolidating = false;
      }
    }

    /**
     * Obtiene la variante correcta desde la API
     */
    async getCorrectVariant(productId, quantity) {
      try {
        const apiResponse = await this.originalFetch(this.API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: `gid://shopify/Product/${productId}`,
            quantity: quantity,
            SHOPIFY_STORE: 'abast-comercial-parral.myshopify.com'
          })
        });

        if (!apiResponse.ok) {
          return null;
        }

        const data = await apiResponse.json();

        if (!data.variantId) {
          return null;
        }

        return data.variantId.split('/').pop();
      } catch (error) {
        console.error('   ❌ Error getting correct variant:', error);
        return null;
      }
    }

    /**
     * Actualiza la variante de un producto cuando la cantidad cambió
     */
    async updateProductVariant(productId, currentItem, correctVariantId, totalQuantity) {
      try {
        console.log('🔧 === VARIANT UPDATE START ===');
        console.log('   📦 Product ID:', productId);
        console.log('   📊 Current item:', {
          variant_id: currentItem.variant_id,
          variant_title: currentItem.variant_title,
          quantity: currentItem.quantity
        });
        console.log('   🎯 Correct variant ID:', correctVariantId);

        // Eliminar el item actual
        const itemKey = currentItem.key || `${currentItem.variant_id}:${currentItem.id}`;

        console.log(`   🗑️ Removing current item with key: ${itemKey}`);

        const removeResponse = await this.originalFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: itemKey,
            quantity: 0
          })
        });

        if (!removeResponse.ok) {
          const errorText = await removeResponse.text();
          console.error(`   ❌ Failed to remove: ${removeResponse.status} - ${errorText}`);
          return false;
        }

        console.log('   ✓ Removed current item');

        // Agregar con la variante correcta
        console.log('   ➕ Adding correct variant...');

        const addResponse = await this.originalFetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: correctVariantId, quantity: totalQuantity })
        });

        const addResult = await addResponse.json();

        console.log('   ➕ Added:', {
          variant_id: addResult.variant_id,
          variant_title: addResult.variant_title,
          quantity: addResult.quantity
        });
        console.log('✅ === VARIANT UPDATE COMPLETE ===');

        return true;

      } catch (error) {
        console.error('❌ === VARIANT UPDATE ERROR ===');
        console.error('   Error:', error);
        return false;
      }
    }
  
    /**
     * AUTO-CONSOLIDACIÓN en página del carrito
     */
    async autoConsolidateCartPage() {
      if (this.autoConsolidated) return;
      this.autoConsolidated = true;
  
      try {
        console.log('🔍 Checking for duplicates in cart...');
        
        const cart = await this.originalFetch('/cart.js').then(r => r.json());
        
        if (cart.item_count === 0) {
          console.log('   ℹ️ Cart is empty');
          return;
        }
  
        const byProduct = {};
        cart.items.forEach(item => {
          if (!byProduct[item.product_id]) {
            byProduct[item.product_id] = [];
          }
          byProduct[item.product_id].push(item);
        });
  
        const duplicates = Object.entries(byProduct).filter(([_, items]) => items.length > 1);
  
        if (duplicates.length === 0) {
          console.log('   ✅ No duplicates found');
          return;
        }
  
        console.log(`   ⚠️ Found ${duplicates.length} product(s) with duplicates`);
        
        for (const [productId, items] of duplicates) {
          await this.consolidateProduct(productId, items, true);
        }
  
        console.log('   ✅ Auto-consolidation complete, reloading...');
        setTimeout(() => window.location.reload(), 500);
  
      } catch (error) {
        console.error('   ❌ Error in auto-consolidation:', error);
      }
    }
  
    /**
     * Consolida variantes de un producto
     */
    async consolidateProduct(productId, items, silent = false) {
      try {
        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

        if (!silent) {
          console.log('🔧 === CONSOLIDATION START ===');
          console.log('   📦 Product ID:', productId);
          console.log('   📊 Items to consolidate:', items.map(i => ({
            variant_id: i.variant_id,
            variant_title: i.variant_title,
            quantity: i.quantity
          })));
          console.log('   📊 Total quantity:', totalQuantity);
          console.log('   🌐 Calling API...');
        }

        const apiResponse = await this.originalFetch(this.API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: `gid://shopify/Product/${productId}`,
            quantity: totalQuantity,
            SHOPIFY_STORE: 'abast-comercial-parral.myshopify.com'
          })
        });

        if (!apiResponse.ok) {
          console.warn('   ⚠️ API error:', apiResponse.status, apiResponse.statusText);
          return false;
        }

        const data = await apiResponse.json();

        if (!silent) {
          console.log('   📡 API Response:', data);
        }

        if (!data.variantId) {
          if (!silent) console.log('   ℹ️ No variant rule - skipping consolidation');
          return false;
        }

        const correctVariantId = data.variantId.split('/').pop();

        if (!silent) {
          console.log('   🎯 Correct variant ID:', correctVariantId);
          console.log('   🗑️ Removing existing variants...');
        }

        // Eliminar todas las variantes existentes usando el 'key' de cada item
        for (const item of items) {
          // Shopify usa 'key' (o 'id' como string) para identificar items en el carrito
          const itemKey = item.key || `${item.variant_id}:${item.id}`;

          if (!silent) {
            console.log(`      🗑️ Removing item with key: ${itemKey}`);
          }

          const removeResponse = await this.originalFetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: itemKey,  // Usar key en lugar de variant_id
              quantity: 0
            })
          });

          if (!removeResponse.ok) {
            const errorText = await removeResponse.text();
            console.error(`      ❌ Failed to remove ${itemKey}: ${removeResponse.status} - ${errorText}`);
          } else {
            if (!silent) {
              console.log(`      ✓ Removed variant ${item.variant_id} (${item.variant_title})`);
            }
          }
        }

        // Agregar consolidada
        if (!silent) console.log('   ➕ Adding consolidated variant...');

        const addResponse = await this.originalFetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: correctVariantId, quantity: totalQuantity })
        });

        const addResult = await addResponse.json();

        if (!silent) {
          console.log('   ➕ Added:', {
            variant_id: addResult.variant_id,
            variant_title: addResult.variant_title,
            quantity: addResult.quantity
          });
          console.log('✅ === CONSOLIDATION COMPLETE ===');
        }

        return true;

      } catch (error) {
        console.error('❌ === CONSOLIDATION ERROR ===');
        console.error('   Error:', error);
        console.error('   Stack:', error.stack);
        return false;
      }
    }
  
    /**
     * Actualiza UI del carrito
     */
    async updateCartUI(cart) {
      try {
        if (!cart) {
          cart = await this.originalFetch('/cart.js').then(r => r.json());
        }

        console.log('🔄 Updating cart UI...', { item_count: cart.item_count });

        // Actualizar contador - SIN destruir el icono
        const cartCountBubble = document.querySelector('.cart-count-bubble');
        if (cartCountBubble) {
          const countSpan = cartCountBubble.querySelector('span') || cartCountBubble.querySelector('.cart-count');
          if (countSpan) {
            countSpan.textContent = cart.item_count;
          }
        }

        // Buscar otros contadores sin destruir su estructura
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.setAttribute('data-cart-count', cart.item_count);
          // Si el elemento solo contiene el número, actualízalo
          if (el.childNodes.length === 1 && el.childNodes[0].nodeType === 3) {
            el.textContent = cart.item_count;
          }
        });

        // Recargar el contenido del cart drawer
        await this.reloadCartDrawer();

        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

        console.log('✅ Cart UI updated');

      } catch (error) {
        console.error('❌ Error updating UI:', error);
      }
    }

    /**
     * Recarga el contenido del cart drawer - MÉTODO SIMPLIFICADO Y ROBUSTO
     */
    async reloadCartDrawer() {
      try {
        console.log('🔄 Reloading cart drawer...');

        const cartDrawer = document.querySelector('cart-drawer');

        if (!cartDrawer) {
          console.log('   ⚠️ No cart-drawer element found');
          return;
        }

        const wasOpen = cartDrawer.classList.contains('active') ||
                       cartDrawer.classList.contains('is-open') ||
                       cartDrawer.hasAttribute('open');

        console.log('   📦 Drawer is', wasOpen ? 'open' : 'closed');

        // MÉTODO 1: Fetch directo de la página y extraer cart-drawer actualizado
        try {
          console.log('   🌐 Fetching fresh cart drawer HTML...');

          // Obtener el HTML completo de la página actual
          const response = await this.originalFetch(window.location.pathname + window.location.search, {
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });

          if (response.ok) {
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // Buscar el cart-drawer en el HTML fresco
            const freshCartDrawer = doc.querySelector('cart-drawer');

            if (freshCartDrawer) {
              console.log('   ✅ Found fresh cart-drawer in HTML');

              // Reemplazar el contenido completo
              cartDrawer.innerHTML = freshCartDrawer.innerHTML;

              // Restaurar estado
              if (wasOpen) {
                cartDrawer.classList.add('active');
                cartDrawer.setAttribute('open', '');
                console.log('   ✅ Drawer content updated and reopened');
              } else {
                console.log('   ✅ Drawer content updated');
              }

              return;
            } else {
              console.warn('   ⚠️ No cart-drawer found in fetched HTML');
            }
          }
        } catch (e) {
          console.warn('   ⚠️ Direct HTML fetch failed:', e.message);
        }

        // MÉTODO 2: Usar eventos nativos del cart drawer
        try {
          console.log('   🔧 Trying native cart drawer methods...');

          // Disparar evento de actualización del carrito
          const cartUpdatedEvent = new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { source: 'cart-consolidation' }
          });
          document.dispatchEvent(cartUpdatedEvent);

          // Si el drawer tiene método refresh o reload
          if (typeof cartDrawer.refresh === 'function') {
            console.log('   📝 Using drawer.refresh()');
            cartDrawer.refresh();
            return;
          }

          if (typeof cartDrawer.renderContents === 'function') {
            console.log('   📝 Using drawer.renderContents()');
            const cart = await this.originalFetch('/cart.js').then(r => r.json());
            cartDrawer.renderContents(cart);
            return;
          }
        } catch (e) {
          console.warn('   ⚠️ Native methods failed:', e.message);
        }

        // MÉTODO 3: Forzar recarga completa cerrando y reabriendo
        console.log('   🔄 Forcing complete drawer reload...');

        if (wasOpen) {
          // Cerrar el drawer
          if (typeof cartDrawer.close === 'function') {
            cartDrawer.close();
          } else {
            cartDrawer.classList.remove('active', 'is-open', 'animate');
            cartDrawer.removeAttribute('open');
          }

          // Esperar un momento y hacer fetch de cart.js para forzar actualización
          await new Promise(resolve => setTimeout(resolve, 300));

          // Obtener datos actualizados del carrito
          const freshCart = await this.originalFetch('/cart.js').then(r => r.json());

          // Reabrir el drawer - esto debería forzar un re-render
          if (typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else {
            cartDrawer.classList.add('active', 'animate');
            cartDrawer.setAttribute('open', '');
          }

          console.log('   ✅ Drawer closed and reopened with fresh data');
        }

      } catch (error) {
        console.error('❌ Error reloading cart drawer:', error);
      }
    }
  
    showSuccess(submitButton, originalText) {
      if (!submitButton) return;
  
      const isInput = submitButton.tagName === 'INPUT';
      
      if (isInput) {
        submitButton.value = '✓ Agregado';
      } else {
        submitButton.textContent = '✓ Agregado';
      }
      
      setTimeout(() => {
        submitButton.disabled = false;
        if (isInput) {
          submitButton.value = originalText || 'Agregar';
        } else {
          submitButton.textContent = originalText || 'Agregar';
        }
      }, 2000);
    }
  
    showError(form, message) {
      const submitButton = form.querySelector('[type="submit"], [name="add"]');
      if (!submitButton) return;
  
      const isInput = submitButton.tagName === 'INPUT';
      
      if (isInput) {
        submitButton.value = '❌ Error';
      } else {
        submitButton.textContent = '❌ Error';
      }
      
      setTimeout(() => {
        submitButton.disabled = false;
        if (isInput) {
          submitButton.value = 'Agregar';
        } else {
          submitButton.textContent = 'Agregar';
        }
      }, 3000);
    }
  
    /**
     * Consolidación manual
     */
    async manualConsolidate() {
      console.log('🔄 Manual consolidation...');
      this.autoConsolidated = false;
      await this.autoConsolidateCartPage();
    }
  }
  
  // Inicializar
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.cartConsolidation = new CartConsolidation();
    });
  } else {
    window.cartConsolidation = new CartConsolidation();
  }
  
  // Función global
  window.consolidateCart = function() {
    if (window.cartConsolidation) {
      window.cartConsolidation.manualConsolidate();
    }
  };
  
  console.log('💡 Functions: consolidateCart()');
  console.log('💡 Debug: window.cartConsolidation');
  console.log('✨ v5.0 - Auto-updates variant when quantity changes');